import { useEffect, useId, useRef, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/ui/button";

interface ConfirmDialogProps {
  open: boolean;
  title: string;
  description: string;
  confirmLabel: string;
  isDangerous?: boolean;
  isPending?: boolean;
  isConfirmDisabled?: boolean;
  hideConfirmButton?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
  children?: ReactNode;
}

/** Shared confirmation dialog that can also host a form for a destructive action. */
export function ConfirmDialog({
  open,
  title,
  description,
  confirmLabel,
  isDangerous = false,
  isPending = false,
  isConfirmDisabled = false,
  hideConfirmButton = false,
  onConfirm,
  onCancel,
  children,
}: ConfirmDialogProps) {
  const { t } = useTranslation();
  const titleId = useId();
  const descriptionId = useId();
  const dialogRef = useRef<HTMLDivElement>(null);
  const cancelButtonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!open) return undefined;

    cancelButtonRef.current?.focus();

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        if (!isPending) onCancel();
        return;
      }
      if (event.key !== "Tab") return;

      const focusableElements = dialogRef.current?.querySelectorAll<HTMLElement>(
        "button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), a[href], [tabindex]:not([tabindex='-1'])",
      );
      if (!focusableElements?.length) return;

      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];
      if (!firstElement || !lastElement) return;
      if (event.shiftKey && document.activeElement === firstElement) {
        event.preventDefault();
        lastElement.focus();
      } else if (!event.shiftKey && document.activeElement === lastElement) {
        event.preventDefault();
        firstElement.focus();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isPending, onCancel, open]);

  if (!open) return null;

  return createPortal(
    <div
      role="alertdialog"
      aria-modal="true"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget && !isPending) onCancel();
      }}
      aria-labelledby={titleId}
      aria-describedby={descriptionId}
      className="fixed inset-0 z-[70] flex items-center justify-center bg-overlay/80 p-4"
    >
      <div
        ref={dialogRef}
        className="flex w-full max-w-lg flex-col gap-4 rounded-lg border border-border bg-bg-elevated p-6 shadow-lg"
      >
        <div className="flex flex-col gap-1">
          <h2 id={titleId} className="text-base font-semibold text-text-primary">
            {title}
          </h2>
          <p id={descriptionId} className="text-sm text-text-muted">
            {description}
          </p>
        </div>
        {children}
        <div className="flex flex-col-reverse sm:flex-row sm:justify-end sm:gap-2">
          <Button
            ref={cancelButtonRef}
            type="button"
            variant="outline"
            className="bg-bg-elevated text-base shadow-sm hover:bg-bg-surface hover:text-text-primary focus-visible:ring-1 focus-visible:ring-offset-0"
            onClick={onCancel}
            disabled={isPending}
          >
            {t("common:actions.cancel")}
          </Button>
          {hideConfirmButton ? null : (
            <Button
              type="button"
              variant={isDangerous ? "danger" : "primary"}
              className="text-base shadow-sm focus-visible:ring-1 focus-visible:ring-offset-0"
              onClick={onConfirm}
              disabled={isPending || isConfirmDisabled}
            >
              {confirmLabel}
            </Button>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}
